import { GridMode, RowWithMetadata, SetRowDataFn } from '@client-shared/gridEdit/useEditableGridLogic';
import {
  adminColumnDefinitionFactory,
  buildEditableGridColumnDefinitions,
  defaultColDefProperties,
  EditableWhenConfig,
  WithDropdownConfig,
} from '../../../shared/gridEdit/columnDefinitionsUtils';

import type { ScenarioDataSources, ScenarioMember, ScenariosMembersListLookupData } from '@queries/scenarios';

import {
  businessColumnDefinition,
  businessUnitColumnDefinition,
  companyCodeColumnDefinition,
  companyCodeDescriptionColumnDefinition,
  connectionNameColumnDefinition,
  countryNameColumnDefinition,
  functionalCurrencyColumnDefinition,
  legalEntityColumnDefinition,
  recordFTCColumnDefinition,
  secondaryTaxColumnDefinition,
  statCalcColumnDefinition,
  statCurrencyColumnDefinition,
  statusColumnDefinition,
  subledgerFlagColumnDefinition,
  taxCurrencyColumnDefinition,
} from '../../../shared/columnDefinitions';

import ScenarioMemberAdminColumnRenderer from './AdminColumnRenderer.component';
import CountryCellRenderer from './CountryCellRenderer.component';
import ObligationCompanyCodeCellRenderer from './ObligationCompanyCodeCellRenderer.component';
import { selectionCheckboxColumnFactory, UseSelectableRowGridLogicApi } from '@client-shared/agGrid/selectableRows';
import { ColumnDefinition } from '@common/xlsxExport/types';

const baseColumnDefinitions: ({
  baseColumnDefinition: ColumnDefinition,
  editableWhen?: GridMode[],
  editableWhenConfig?: EditableWhenConfig<RowWithMetadata<ScenarioMember>, any, {}>,
  withDropdown?: boolean,
  withDropdownConfig?: WithDropdownConfig<ScenariosMembersListLookupData>,
})[] = [
  {
    baseColumnDefinition: {
      headerName: 'Included in Scenario',
      field: 'scenarioEnabled',
      width: 80,
      minWidth: 80,
    },
    withDropdown: true,
    withDropdownConfig: {
      optionsGetter: context => context.yesNoOptions,
    },
  },
  {
    baseColumnDefinition: {
      ...companyCodeColumnDefinition,
      cellRenderer: ObligationCompanyCodeCellRenderer,
    },
    editableWhen: [GridMode.Add],
  },
  {
    baseColumnDefinition: companyCodeDescriptionColumnDefinition,
    editableWhen: [GridMode.Add, GridMode.Edit],
  },
  {
    baseColumnDefinition: legalEntityColumnDefinition,
    editableWhen: [GridMode.Add, GridMode.Edit],
  },
  {
    baseColumnDefinition: businessUnitColumnDefinition,
    editableWhen: [GridMode.Add, GridMode.Edit],
    withDropdown: true,
    withDropdownConfig: {
      optionsGetter: context => context.businessUnitOptions,
    },
  },
  {
    baseColumnDefinition: connectionNameColumnDefinition,
    editableWhen: [GridMode.Add, GridMode.Edit],
    withDropdown: true,
    withDropdownConfig: {
      optionsGetter: context => context.connectionsOptions,
    },
  },
  {
    baseColumnDefinition: {
      ...countryNameColumnDefinition,
      headerName: 'Country Name',
      field: 'associatedLeCountryCode',
    },
    editableWhen: [GridMode.Add, GridMode.Edit],
    editableWhenConfig: {
      cellRenderer: CountryCellRenderer,
    },
    withDropdown: true,
    withDropdownConfig: {
      optionsGetter: context => context.countriesOptions,
    },
  },
  {
    baseColumnDefinition: {
      ...businessColumnDefinition,
      field: 'businessCode',
    },
    editableWhen: [GridMode.Add, GridMode.Edit],
    withDropdown: true,
    withDropdownConfig: {
      optionsGetter: context => context.businessesOptions,
    },
  },
  {
    baseColumnDefinition: statusColumnDefinition,
  },
  {
    baseColumnDefinition: functionalCurrencyColumnDefinition,
    withDropdown: true,
    withDropdownConfig: {
      optionsGetter: context => context.currencyOptions,
    },
    editableWhen: [GridMode.Add, GridMode.Edit],
  },
  {
    baseColumnDefinition: recordFTCColumnDefinition,
    withDropdown: true,
    withDropdownConfig: {
      optionsGetter: context => context.yesNoOptions,
    },
    editableWhen: [GridMode.Add, GridMode.Edit],
  },
  {
    baseColumnDefinition: subledgerFlagColumnDefinition,
    withDropdown: true,
    withDropdownConfig: {
      optionsGetter: context => context.yesNoOptions,
    },
  },
  {
    baseColumnDefinition: {
      ...taxCurrencyColumnDefinition,
      field: 'overrideTaxCurrency',
    },
    withDropdown: true,
    withDropdownConfig: {
      optionsGetter: context => context.currencyOptions,
    },
    editableWhen: [GridMode.Add, GridMode.Edit],
  },
  {
    baseColumnDefinition: statCalcColumnDefinition,
    withDropdown: true,
    withDropdownConfig: {
      optionsGetter: context => context.yesNoOptions,
    },
    editableWhen: [GridMode.Add, GridMode.Edit],
  },
  {
    baseColumnDefinition: statCurrencyColumnDefinition,
    withDropdown: true,
    withDropdownConfig: {
      optionsGetter: context => context.currencyOptions,
    },
    editableWhen: [GridMode.Add, GridMode.Edit],
  },
  {
    baseColumnDefinition: {
      ...secondaryTaxColumnDefinition,
      field: 'overrideSecondaryTax',
    },
    withDropdown: true,
    withDropdownConfig: {
      optionsGetter: context => context.secondaryTaxOptions,
    },
    editableWhen: [GridMode.Add, GridMode.Edit],
  },
];


export const getColumnDefinitions = ({ setRowData, lookUpData, editRow, context }: {
  setRowData: SetRowDataFn<RowWithMetadata<ScenarioMember>>,
  lookUpData: ScenariosMembersListLookupData,
  editRow: (data: RowWithMetadata<ScenarioMember>) => void,
  context: {
    mode: GridMode,
    editedRowId: string,
    selectionApi: UseSelectableRowGridLogicApi<ScenarioMember>,
    scenarioDataSource: ScenarioDataSources,
    isScenarioLocked: boolean,
  }
}) => [
  adminColumnDefinitionFactory({
    cellRenderer: ScenarioMemberAdminColumnRenderer,
    editRow,
    context,
  }),
  {
    ...selectionCheckboxColumnFactory<ScenarioMember>({
      selectionApi: context.selectionApi,
      isDisabled: () => context.isScenarioLocked,
    }),
    ...defaultColDefProperties,
  },
  ...buildEditableGridColumnDefinitions({
    baseColumnDefinitions,
    lookUpData,
    setRowData,
    getEntityId: member => member.meta.isAdded ? null : member.companyCode,
  }),
];
